import { Box, Button, LockIcon, Space, Table, Text } from '@/components';
import { ProjectResourceGuard } from '@/features/authentication/_components/ProjectResourceGuard';
import { useProjectsPageSearchQueryStates } from '@/features/project/_hooks/search-projects/search-query-states';
import {
  useGroup,
  useUpdateGroupTargets,
} from '@/features/project/groups/_hooks/group';
import { PublishedLabelModifier } from '@/features/project/projects/_models/project-filter';
import { useId } from '@/hooks';
import { t } from '@/i18n';
import {
  PAGE_SIZE_IN_BACKEND,
  datetimeFormat,
  getSortOrder,
  lowerCamelStringToSnake,
} from '@/utils';
import { ExportOutlined, LoadingOutlined } from '@ant-design/icons/lib/icons';
import { type FC, useCallback, useEffect, useState } from 'react';
import type { z } from 'zod';
import type { GroupProjectListModel } from '../../_models/group';
import { ExportProjectData } from './ExportProjectData';

export type PrintStatus = {
  projectId: string;
  status: 'notRequested' | 'loading' | 'dataReady' | 'printing';
};
type Props = {
  groupInProjects: z.infer<typeof GroupProjectListModel> | undefined;
  isLoading: boolean;
  editPermission: boolean;
};

export const GroupInProjectsTable: FC<Props> = ({
  groupInProjects,
  isLoading,
  editPermission,
}) => {
  const { id } = useId();
  const { searchQueryStates, setSearchQueryStates } =
    useProjectsPageSearchQueryStates();
  const { mutateAsync: updateGroupTargetsAsync } = useUpdateGroupTargets(id);

  // グループ全体のプロジェクトIDを取得（ページネーションの影響を受けない）
  const group = useGroup(id, { projectIds: true });
  const allGroupProjectIds =
    group.data?.groupTargets.map((target) => target.objectId) ?? [];

  const [printStatusByProjectId, setPrintStatusByProjectId] = useState<
    PrintStatus[]
  >([]);

  // PDF出力用の状態を初期化
  useEffect(() => {
    if (groupInProjects) {
      setPrintStatusByProjectId(
        groupInProjects?.results.map((project) => ({
          projectId: project.id,
          status: 'notRequested',
        })),
      );
    }
    return () => {
      setPrintStatusByProjectId([]);
    };
  }, [groupInProjects]);

  const handlePdfExport = useCallback((projectId: string) => {
    setPrintStatusByProjectId((prev) => {
      return prev.map((v) => {
        if (v.projectId !== projectId) {
          return v;
        }
        const currentStatus = v.status;
        if (currentStatus === 'notRequested') {
          return {
            ...v,
            status: 'loading',
          };
        }
        if (currentStatus === 'dataReady') {
          return {
            ...v,
            status: 'printing',
          };
        }
        return v;
      });
    });
  }, []);

  return (
    <>
      <Table
        rowKey="id"
        dataSource={groupInProjects?.results}
        loading={isLoading}
        columns={[
          {
            title: t('project'),
            key: 'name',
            render: (_, record) => {
              return (
                <Space>
                  {record.isLocked && <LockIcon hint={t('locked-project')} />}
                  <Text weight="regular" text={record.name} />
                </Space>
              );
            },
            defaultSortOrder:
              getSortOrder('name', searchQueryStates.groupInProjectsOrdering) ??
              undefined,
            sorter: true,
          },
          {
            title: t('fiscal-year'),
            width: 100,
            key: 'fiscalYear',
            render: (_, record) => record.fiscalYear,
          },
          {
            title: t('publish-ability'),
            key: 'isPublished',
            width: 100,
            render: (_, record) => {
              return (
                <Text
                  text={
                    PublishedLabelModifier[
                      record.isPublished.toString() as keyof typeof PublishedLabelModifier
                    ]
                  }
                  variant="body"
                  weight="regular"
                />
              );
            },
            defaultSortOrder:
              getSortOrder(
                'isPublished',
                searchQueryStates.groupInProjectsOrdering,
              ) ?? undefined,
            sorter: true,
          },
          {
            title: t('ledger-last-output-at'),
            key: 'individualReportLastOutputAt',
            width: 150,
            render: (_, record) => {
              return (
                <Text
                  text={
                    record.individualReportLastOutputAt
                      ? datetimeFormat({
                          datetime: record.individualReportLastOutputAt,
                          format: 'YYYY/MM/DD HH:mm',
                        })
                      : null
                  }
                  variant="body"
                  weight="regular"
                />
              );
            },
            defaultSortOrder:
              getSortOrder(
                'individualReportLastOutputAt',
                searchQueryStates.groupInProjectsOrdering,
              ) ?? undefined,
            sorter: true,
          },
          {
            title: '',
            key: 'actions',
            width: editPermission ? 300 : 160,
            render: (_, record) => {
              const printStatus = printStatusByProjectId.find(
                (v) => v.projectId === record.id,
              )?.status;

              const hiddenExportPdfButton =
                record.fundSheetType === 'local-government' ||
                record.fundSheetType === 'investment' ||
                record.skipAdministrativeReformTeamEvaluation;

              return (
                <Space>
                  {editPermission && (
                    <ProjectResourceGuard>
                      <Button
                        style={{ width: 75 }}
                        onClick={async () => {
                          const currentPageProjectsCount =
                            groupInProjects?.results.length ?? 0;

                          await updateGroupTargetsAsync(
                            allGroupProjectIds.filter((id) => id !== record.id),
                          );

                          // 現在のページに1つ以下のプロジェクトしか残っていない場合、前のページに戻す
                          if (
                            searchQueryStates.groupInProjectsPage > 1 &&
                            currentPageProjectsCount <= 1
                          ) {
                            setSearchQueryStates((prev) => ({
                              groupInProjectsPage: prev.groupInProjectsPage - 1,
                            }));
                          }
                        }}
                      >
                        {t('group-project-operation-release-project')}
                      </Button>
                    </ProjectResourceGuard>
                  )}
                  <Box
                    justifyContent="center"
                    alignItems="center"
                    width="100px"
                    // antの<Flex>はchildrenが空だとdisplay:noneになるのでBox使っている
                    display="flex"
                  >
                    {!hiddenExportPdfButton && (
                      <Button
                        type="link"
                        onClick={() => {
                          handlePdfExport(record.id);
                        }}
                        disabled={
                          printStatus === 'loading' ||
                          printStatus === 'printing'
                        }
                        icon={
                          printStatus === 'loading' ? (
                            <LoadingOutlined />
                          ) : undefined
                        }
                      >
                        {printStatus === 'loading'
                          ? t('loading')
                          : t('export-ledger')}
                      </Button>
                    )}
                  </Box>
                  <Button
                    type="link"
                    icon={<ExportOutlined aria-label="" />}
                    onClick={() => {
                      window.open(`/rs/project/${record.id}`, '_blank');
                    }}
                  >
                    {t('details')}
                  </Button>
                </Space>
              );
            },
          },
        ]}
        pagination={{
          current: Number(searchQueryStates.groupInProjectsPage),
          total: groupInProjects?.count,
          showSizeChanger: false,
          pageSize: PAGE_SIZE_IN_BACKEND,
        }}
        onChange={(pagination, _filters, sorter) => {
          const newSortOrderKey =
            !Array.isArray(sorter) && sorter.order && sorter.columnKey
              ? `${sorter.order === 'descend' ? '-' : ''}${lowerCamelStringToSnake(sorter.columnKey.toString())}`
              : null;
          setSearchQueryStates((prev) => ({
            ...prev,
            groupInProjectsPage: pagination.current ?? 1,
            groupInProjectsOrdering: newSortOrderKey,
          }));
        }}
      />
      {/* PDF生成用 */}
      <div style={{ height: 0, overflow: 'hidden' }}>
        {printStatusByProjectId.map((v) => {
          if (v.status === 'notRequested' || v.status === 'dataReady') {
            return null;
          }
          return (
            <ExportProjectData
              key={`loader-${v.projectId}`}
              projectId={v.projectId}
              status={v.status}
              setPrintStatusByProjectId={setPrintStatusByProjectId}
            />
          );
        })}
      </div>
    </>
  );
};
